import type { ReactElement } from 'react';
import { Flame, Crown, AlertTriangle, Zap, DollarSign } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';

export type SmartTagType = 'hot' | 'vip' | 'high_value' | 'stalled' | 'fast_track';

interface SmartTagBadgeProps {
  type: SmartTagType;
  className?: string;
}

export function getSmartTagsForDeal(deal: {
  amount?: number | string | null | undefined;
  stage?: string | undefined;
  nextActivityDue?: string | null | undefined;
}): SmartTagType[] {
  const tags: SmartTagType[] = [];
  const rawAmt = deal.amount;
  const parsedAmt = typeof rawAmt === 'string' ? Number(rawAmt) : (rawAmt ?? 0);
  const amount = Number.isFinite(parsedAmt) ? parsedAmt : 0;

  if (amount >= 250000) {
    tags.push('vip');
  } else if (amount >= 100000) {
    tags.push('high_value');
  }

  if (deal.stage === 'Negotiation') {
    tags.push('hot');
  }

  if (deal.stage !== 'Won' && deal.stage !== 'Lost' && !deal.nextActivityDue) {
    tags.push('stalled');
  }

  return tags.slice(0, 2);
}

export function SmartTagBadge({ type, className = '' }: SmartTagBadgeProps): ReactElement {
  const { t } = useTranslation();

  const config = {
    hot: {
      label: t('crm.smart_tags.hot'),
      icon: <Flame size={10} className="text-orange-600 shrink-0" />,
      style: 'bg-orange-50 text-orange-700 border-orange-200',
    },
    vip: {
      label: t('crm.smart_tags.vip'),
      icon: <Crown size={10} className="text-amber-600 shrink-0" />,
      style: 'bg-amber-50 text-amber-800 border-amber-200',
    },
    high_value: {
      label: t('crm.smart_tags.high_value'),
      icon: <DollarSign size={10} className="text-emerald-600 shrink-0" />,
      style: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    stalled: {
      label: t('crm.smart_tags.stalled'),
      icon: <AlertTriangle size={10} className="text-red-600 shrink-0" />,
      style: 'bg-red-50 text-red-700 border-red-200',
    },
    fast_track: {
      label: t('crm.smart_tags.fast_track'),
      icon: <Zap size={10} className="text-blue-600 shrink-0" />,
      style: 'bg-blue-50 text-blue-700 border-blue-200',
    },
  }[type];

  return (
    <span
      data-testid={`smart-tag-${type}`}
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold tracking-tight shadow-2xs ${config.style} ${className}`}
    >
      {config.icon}
      <span>{config.label}</span>
    </span>
  );
}
